/**
 * Generic name/handle emit helpers for the reference audit.
 *
 * Package-private to the `referenceAudit*` module family. Every emitter
 * supplies a COMPLETE `IssueContext` (Codex#2 P2-3 — no `Partial<…> as …`
 * casts); the helper adds `code` / `refKind` / `referencedKey` / `message`.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §7.
 */

import { isDefaultArrowheadValue } from "./builtinArrowNames";
import { canonHandle } from "./referenceAudit.resolution";
import {
  ReferenceErrorCode,
  type IssueContext,
  type ReferenceIssue,
} from "./referenceAudit.types";

/**
 * Name-reference check enforcing empty/required/sentinel ORDER:
 *  1. missing / empty FIRST — required ⇒ error regardless of sentinels.
 *  2. non-empty sentinel (arrowhead) ⇒ ok.
 *  3. otherwise resolve case-insensitively against `idx`.
 */
export function checkName(
  key: string | undefined | null,
  idx: ReadonlySet<string>,
  sentinels: ReadonlySet<string>,
  required: boolean,
  code: ReferenceErrorCode,
  ctx: IssueContext,
  errors: ReferenceIssue[],
): void {
  if (key == null || key === "") {
    if (required) {
      errors.push({
        ...ctx,
        code,
        refKind: "name",
        referencedKey: key ?? "",
        message: `required ${ctx.field} is empty/missing`,
      });
    }
    return;
  }
  const k = key.toLowerCase();
  if (sentinels.has(k)) return;
  if (!idx.has(k)) {
    errors.push({
      ...ctx,
      code,
      refKind: "name",
      referencedKey: key,
      message: `${ctx.field} "${key}" not found`,
    });
  }
}

/**
 * Handle-reference check. Optional handles skip when empty/missing;
 * required handles error when empty. Non-empty handles must resolve.
 *
 * P2-A — DXF handles are hexadecimal and case-insensitive; `idx` is built
 * from `canonHandle`-folded handles, so the looked-up handle is folded the
 * SAME way before comparison. The emitted `referencedKey` keeps the ORIGINAL
 * casing so consumers can locate the verbatim source value.
 */
export function checkHandle(
  handle: string | undefined | null,
  idx: ReadonlySet<string>,
  required: boolean,
  code: ReferenceErrorCode,
  ctx: IssueContext,
  errors: ReferenceIssue[],
): void {
  if (handle == null || handle === "") {
    if (required) {
      errors.push({
        ...ctx,
        code,
        refKind: "handle",
        referencedKey: handle ?? "",
        message: `required ${ctx.field} is empty/missing`,
      });
    }
    return;
  }
  if (!idx.has(canonHandle(handle))) {
    errors.push({
      ...ctx,
      code,
      refKind: "handle",
      referencedKey: handle,
      message: `${ctx.field} handle "${handle}" not found`,
    });
  }
}

/**
 * The emission SITE of an MLEADER-family arrowhead value — the discriminator for
 * whether a raw BLOCK_RECORD HANDLE value is tolerated (SL-11 round-7, @codex F3).
 * The two sites export the value DIFFERENTLY:
 *   - `"entity"` — the MLEADER entity / leader-line `arrowheadBlock` (AcDbMLeader
 *     body 342 = arrow_head_handle), copied VERBATIM to the writer. An imported hex
 *     handle points at a live BLOCK_RECORD on reopen → a raw handle IS valid.
 *   - `"style"` — the MLEADERSTYLE `arrowheadBlock` (group 341), resolved NAME→handle
 *     by `resolveMLeaderStyleReferenceHandles` and OMITTED when the NAME has no live
 *     BLOCK_RECORD. A raw handle value never resolves by name → the group is DROPPED
 *     → it dangles on reopen, so it must be FLAGGED (only a resolvable NAME is valid).
 */
export type MleaderArrowheadSite = "entity" | "style";

/**
 * MLEADER / MLEADERSTYLE arrowhead-block ref check — DISTINCT from the DIMENSION
 * DSTYLE arrowhead fields (SL-11 round-6, @codex).
 *
 * In NEITHER site is a built-in arrow SYNTHESIZED on export (unlike DIMENSION
 * DSTYLE, whose `stampDimensionDStyleArrowheadHandles` canonicalizes + auto-creates
 * an absent `_<NAME>` arrow block). So on reopen the value denotes a real arrow ONLY
 * when it is the closed-filled/null default, or LITERALLY matches a declared block
 * NAME — plus, for the `"entity"` site ONLY, a live BLOCK_RECORD handle (the body-342
 * value is written verbatim, so an imported handle points at a real block). An
 * unprefixed/underscored built-in arrow name (`OPEN90` / `_DOT`) with NO backing
 * block is NOT auto-materialized — it exports dropped/invalid — so the audit FLAGs it
 * (do NOT whitelist it via the DIMENSION arrowhead sentinel set, which tolerates
 * every built-in arrow name).
 *
 * SL-11 round-7 (@codex F3) — the `site` splits the contract. The MLEADERSTYLE
 * (`"style"`) export resolves the value by NAME→handle and DROPS group 341 when the
 * name is unknown, so a raw BLOCK_RECORD handle (e.g. a hex `A9` with no block NAMED
 * `A9`) would be DROPPED → dangling; it must be FLAGGED. The MLEADER-entity
 * (`"entity"`) export writes the value verbatim, so a live handle round-trips and is
 * tolerated (round-6 behavior, preserved).
 *
 * `blockNames` = `res.names.blocks` (lowercased); `blockRecordHandles` =
 * `res.handles.blockRecords` (canonicalised) — the audit already carries both.
 */
export function checkMleaderArrowheadRef(
  value: string | undefined | null,
  blockNames: ReadonlySet<string>,
  blockRecordHandles: ReadonlySet<string>,
  site: MleaderArrowheadSite,
  code: ReferenceErrorCode,
  ctx: IssueContext,
  errors: ReferenceIssue[],
): void {
  // Unset → the default arrow (no ref). Empty is handled by the "" branch, never a
  // sentinel; a set closed-filled/null spelling ("." / "_closedfilled" / "0") is the
  // default too — the ONLY tolerated sentinels here (no built-in-arrow whitelist).
  if (value == null || value === "" || isDefaultArrowheadValue(value)) return;
  const key = value.toLowerCase();
  // A declared block NAME resolves on reopen at BOTH sites (verbatim body-342 or
  // NAME→341-handle lookup). No built-in `_<NAME>` synthesis.
  if (blockNames.has(key)) return;
  // A live BLOCK_RECORD handle resolves ONLY for the verbatim-export `"entity"` site
  // (body-342 points at a real block). The `"style"` site resolves by NAME and DROPS
  // 341 for a raw handle → it must NOT pass here (falls through to the flag below).
  if (site === "entity" && blockRecordHandles.has(canonHandle(value))) return;
  // A built-in arrow name with no real block / a custom name / a dangling handle
  // (or, at the `"style"` site, a raw BLOCK_RECORD handle) → exported verbatim /
  // dropped → dangles on reopen. Flag it (matches real export behavior).
  errors.push({
    ...ctx,
    code,
    refKind: "name",
    referencedKey: value,
    message: `${ctx.field} "${value}" not found`,
  });
}
